import payform from 'payform';
import '../types-internal/index.js';

/**
 * Wrapper of payform.validateCardNumber to validate credit card numbers
 *
 * @param cardNumber string
 * @return boolean if credit card number is valid
 */
const isCardNumber = function (cardNumber) {
    return payform.validateCardNumber(cardNumber);
};
const deleteCardArrayAndUpdate = (creditCards, cardToDelete, newSelectedCardPspIdentifier) => {
    const newCreditCards = creditCards.slice();
    const deletionIndex = newCreditCards.indexOf(cardToDelete);
    if (deletionIndex >= 0) {
        newCreditCards.splice(deletionIndex, 1);
    }
    if (newSelectedCardPspIdentifier) {
        const newSelectedCardIndex = newCreditCards.findIndex((card) => card.pspIdentifier === newSelectedCardPspIdentifier);
        if (newSelectedCardIndex !== -1) {
            newCreditCards[newSelectedCardIndex] = {
                ...newCreditCards[newSelectedCardIndex],
                selected: true,
            };
        }
    }
    return newCreditCards;
};
const selectCardAndUnselectPrevious = (currentCards, pspIdentifier) => {
    const newCreditCards = currentCards
        .slice()
        .map((c) => c.pspIdentifier === pspIdentifier
        ? { ...c, selected: true }
        : c.selected
            ? { ...c, selected: false }
            : c);
    return newCreditCards;
};
/**
 * Convert string to a format that there's space between every 4 characters
 * <p>For example: DE12444 to DE12 4444
 *
 * @param text Iban number or credit card number
 * @return string in new format
 */
const checkBancontactCardNumber = (cardNumber) => {
    if (cardNumber !== '' && cardNumber.length < 19) {
        return false;
    }
    else if (!cardNumber.match(/^(6703|4871)/)) {
        return true;
    }
    return true;
};
/**
 * Convert string to a format that there's space between every 4 characters
 * <p>For example: DE12444 to DE12 4444
 *
 * @param text Iban number or credit card number
 * @param onlyNumbers in case of credit card number it should return only numbers
 * @return string in new format
 */
const formatWithSpaces = (text, onlyNumbers) => {
    const inputNumbersOnly = text.replace(onlyNumbers ? /\D/gi : /\W/gi, '');
    const splits = inputNumbersOnly.match(/.{1,4}/g);
    let spacedNumber = '';
    if (splits) {
        spacedNumber = splits.join(' ');
    }
    return spacedNumber;
};
/**
 * Prevents the cursor within an input that uses `formatWithSpaces` from jumping to the end
 * if the user puts the cursor in the middle and starts typing from there.
 *
 * @param target Event target
 * @return string formatted value
 */
const persistCursorPosForSpacedInputValue = (target, onlyNumbers) => {
    const value = formatWithSpaces(target.value, onlyNumbers);
    let position = target.selectionStart || 0;
    if (target.value.length !== value.length && position === target.value.length) {
        position = value.length;
    }
    target.value = value;
    target.selectionEnd = position;
    return value;
};
/**
 * Convert string to accept only numbers
 *
 * @param text any string
 *
 * @return string with only numbers
 */
const acceptOnlyNumber = (text) => {
    return text.replace(/\D/gi, '');
};
/**
 * Checks if the string only contains non numeric characters
 *
 * @param text any string
 *
 * @return boolean if the string is valid
 */
const isNonNumeric = (text) => {
    return /^[A-Za-z]+( [A-Za-z]+){0,2}$/.test(text);
};
/**
 * Check whether provided date(month, year) is valid
 *
 * @param month 2 digit string from 01 to 12
 * @param year 2 digit string
 *
 * @return boolean to show whether date is valid
 */
const validateDate = (month, year) => {
    if (!month || !year || ['00', '0'].includes(month) || ['00', '0'].includes(year))
        return false;
    const curDate = new Date();
    const expiryDate = new Date(year.length === 2 ? Number('20' + year) : Number(year), Number(month));
    const maxFutureDate = new Date(curDate.getFullYear() + 15, 12);
    expiryDate.setMonth(expiryDate.getMonth() - 1);
    expiryDate.setMonth(expiryDate.getMonth() + 1, 1);
    return expiryDate > curDate && expiryDate <= maxFutureDate;
};
/**
 * Check whether provided IBAN is valid
 * based on the official algorithm for IBAN validation https://en.wikipedia.org/wiki/International_Bank_Account_Number#Algorithms
 * @param iban string representing the IBAN input value
 *
 * @return boolean to show whether IBAN is valid
 */
const isIbanValid = (iban) => {
    const ibanStripped = iban
        .replace(/[^A-Z0-9]+/gi, '') //keep numbers and letters only
        .toUpperCase(); //calculation expects upper-case
    const m = ibanStripped.match(/^([A-Z]{2})([0-9]{2})([A-Z0-9]{9,30})$/);
    if (!m)
        return false;
    const numbericed = (m[3] + m[1] + m[2]).replaceAll(/[A-Z]/g, (ch) => {
        //replace upper-case characters by numbers 10 to 35
        return `${ch.charCodeAt(0) - 55}`;
    });
    //The resulting number would be to long for javascript to handle without loosing precision.
    //So the trick is to chop the string up in smaller parts.
    const mod97 = numbericed.match(/\d{1,7}/g)?.reduce((total, curr) => {
        return `${Number(total + curr) % 97}`;
    }, '');
    return Number(mod97) === 1;
};
/**
 * Get the last 4 digits of an IBAN
 * @param iban string representing the IBAN input value
 *
 * @return string last 4 digits of the IBAN
 */
const getIBANLastFourDigits = (iban) => {
    if (!iban)
        return '';
    const ibanStripped = iban
        ? iban.replace(/[^A-Z0-9]+/gi, '') //keep numbers and letters only
        : '';
    return ibanStripped.substring(ibanStripped.length - 4);
};
/**
 * Returns credit card identifier if selected payment is saved card
 *
 * @param selectedPayment - represents the selected payment method in the checkout
 *
 * @return A string representing card unique identifier or empty if selected payment isn't saved card
 */
const getSelectedCardIdentifier = (selectedPayment) => {
    if (selectedPayment?.paymentMethodShortName &&
        !selectedPayment?.order &&
        ['K', 'BC'].includes(selectedPayment?.paymentMethodShortName))
        return selectedPayment?.details?.cards?.[0].pspIdentifier || '';
    return '';
};

export { acceptOnlyNumber, checkBancontactCardNumber, deleteCardArrayAndUpdate, formatWithSpaces, getIBANLastFourDigits, getSelectedCardIdentifier, isCardNumber, isIbanValid, isNonNumeric, persistCursorPosForSpacedInputValue, selectCardAndUnselectPrevious, validateDate };
//# sourceMappingURL=cardUtils.js.map
